import { useState } from 'react';
import { cn } from '@/lib/utils/cn';

type BrandLogoVariant = 'login' | 'navigation' | 'mobile';

interface BrandLogoProps {
  name?: string | null;
  logoUrl?: string | null;
  variant?: BrandLogoVariant;
}

interface BrandLogoSize {
  mark: string;
  text: string;
  logo: string;
  /** Intrinsic size hints reserving the image box before load, matching the previous markup. */
  logoWidth?: number;
  logoHeight?: number;
}

const sizes: Record<BrandLogoVariant, BrandLogoSize> = {
  login: { mark: 'h-12 w-12', text: 'text-2xl', logo: 'h-12 max-w-full' },
  navigation: {
    mark: 'h-8 w-8',
    text: 'text-lg',
    logo: 'h-auto max-h-12 w-[140px]',
    logoWidth: 140,
    logoHeight: 51,
  },
  mobile: {
    mark: 'h-7 w-7',
    text: 'text-base',
    logo: 'h-auto max-h-[44px] w-[120px]',
    logoWidth: 120,
    logoHeight: 44,
  },
};

function BrandLogoContent({ name, logoUrl, variant = 'login' }: BrandLogoProps) {
  const [hasFailed, setHasFailed] = useState(false);
  const label = name?.trim() || 'InstallFlow';
  const size = sizes[variant];

  if (logoUrl && !hasFailed) {
    return (
      <img
        src={logoUrl}
        alt={label}
        width={size.logoWidth}
        height={size.logoHeight}
        className={cn('object-contain', size.logo)}
        onError={() => setHasFailed(true)}
      />
    );
  }

  return (
    <span role="img" aria-label={label} className="inline-flex max-w-full items-center gap-2">
      <img
        src={`${import.meta.env.BASE_URL}branding/install-flow-mark.png`}
        alt=""
        width={48}
        height={48}
        className={cn('shrink-0 object-contain', size.mark)}
      />
      <span className={cn('truncate font-semibold tracking-tight text-foreground', size.text)}>
        {label}
      </span>
    </span>
  );
}

export function BrandLogo({ logoUrl, ...props }: BrandLogoProps) {
  const source = logoUrl?.trim() || null;
  return <BrandLogoContent key={source} logoUrl={source} {...props} />;
}
